import { ApiError } from '../../lib/api-error'
import { supabase } from '../../lib/supabase'

/**
 * The single HTTP entry point for the whole application.
 *
 * Orval generates every API call as a thunk that hands us a RELATIVE url and a
 * RequestInit, so this is the one place that knows about base URLs, auth
 * headers, and error shapes. Per the spec, hand-written fetch calls are
 * forbidden -- if an endpoint is missing, regenerate the client instead.
 */

/** Empty string means "same origin", which is the normal case. */
const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? ''

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) return response.json()
  return response.text()
}

export { ApiError }

export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  const headers = new Headers(options.headers)

  // getSession() returns the stored session and transparently refreshes the
  // access token if it has expired, so the header we attach is always valid.
  const { data } = await supabase.auth.getSession()
  const accessToken = data.session?.access_token
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  // Plain concatenation, NOT `new URL(url)`: these urls are relative, and the
  // URL constructor throws on a relative string without a base.
  const response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers })
  const body = await readBody(response)

  if (!response.ok) {
    throw new ApiError(response.status, body)
  }

  return body as T
}

export default customFetch
